<template>
  <div class="space-y-6">
    <!-- Toolbar -->
    <div class="flex items-center gap-3">
      <woot-input
        v-model="q"
        :placeholder="$t('IMAGE_LIBRARY.SEARCH_PLACEHOLDER')"
        class="w-80"
        @input="onSearch"
      />
      <div class="text-xs text-gray-500 mt-1">{{ $t('IMAGE_LIBRARY.SEARCH_HELP') }}</div>
      <woot-dropdown v-model="sort" :options="sortOptions" />
      <div class="text-xs text-gray-500 mt-1">{{ $t('IMAGE_LIBRARY.SORT_HELP') }}</div>
      <div class="flex-1" />
      <woot-button v-if="mode === 'library'" icon="add" @click="openCreateLibrary">
        {{ $t('IMAGE_LIBRARY.ADD_FOLDER') }}
      </woot-button>
      <woot-button v-else icon="arrow-upload" @click="openUpload">
        {{ $t('IMAGE_LIBRARY.UPLOAD_IMAGES') }}
      </woot-button>
    </div>

    <!-- Breadcrumb -->
    <div v-if="mode === 'item'" class="text-sm text-gray-600">
      <router-link :to="basePath" class="text-indigo-600 hover:underline">{{ $t('IMAGE_LIBRARY.TITLE') }}</router-link>
      <span class="mx-1">›</span>
      <span>{{ currentLibrary?.name }}</span>
    </div>

    <!-- Grid duy nhất -->
    <ImageGrid
      :mode="mode"
      :items="items"
      :is-loading="isLoading"
      :page="page"
      :total="total"
      :page-size="12"
      @card-click="onCardClick"
      @page-change="onPageChange"
      @action="onAction"
    />

    <!-- Help section -->
    <div v-if="mode==='library'" class="bg-purple-50 border border-purple-200 rounded-lg p-4">
      <div class="font-medium text-purple-900 mb-1">{{ $t('IMAGE_LIBRARY_HELP.TITLE') }}</div>
      <p class="text-sm text-purple-800 mb-2">{{ $t('IMAGE_LIBRARY_HELP.INTRO') }}</p>
      <ul class="list-disc pl-5 text-sm text-purple-900 space-y-1">
        <li>{{ $t('IMAGE_LIBRARY_HELP.POINTS.CREATE_AND_ACTIVE') }}</li>
        <li>{{ $t('IMAGE_LIBRARY_HELP.POINTS.UPLOAD_AND_TAGS') }}</li>
        <li>{{ $t('IMAGE_LIBRARY_HELP.POINTS.TOGGLE_VISIBILITY') }}</li>
        <li>{{ $t('IMAGE_LIBRARY_HELP.POINTS.COPY_URL') }}</li>
        <li>{{ $t('IMAGE_LIBRARY_HELP.POINTS.AI_USAGE') }}</li>
        <li>{{ $t('IMAGE_LIBRARY_HELP.POINTS.LIMIT') }}</li>
      </ul>
    </div>

    <!-- Modals -->
    <LibraryModal
      :show="showLibraryModal"
      :account-id="accountId"
      :library="editingLibrary"
      :business-categories="businessCategories"
      @close="showLibraryModal=false"
      @saved="showLibraryModal=false; load()"
      @category-created="onCategoryCreated"
    />
    <ItemUploadModal
      :show="showUploadModal"
      :account-id="accountId"
      :library-id="libraryId"
      @close="showUploadModal=false"
      @uploaded="showUploadModal=false; load()"
    />
    <ItemEditModal
      :show="showEditItemModal"
      :account-id="accountId"
      :library-id="libraryId"
      :item="editingItem"
      @close="showEditItemModal=false"
      @saved="showEditItemModal=false; load()"
    />
  </div>
</template>

<script>
import { frontendURL } from 'dashboard/helper/URLHelper'
import ImageGrid from './ImageGrid.vue'
import LibraryModal from './LibraryModal.vue'
import ItemUploadModal from './ItemUploadModal.vue'
import ItemEditModal from './ItemEditModal.vue'

export default {
  name: 'ImageLibraryTab',
  components: { ImageGrid, LibraryModal, ItemUploadModal, ItemEditModal },
  data() {
    return {
      isLoading: false,
      mode: 'library', // 'library' | 'item'
      items: [],
      total: 0,
      page: 1,
      q: '',
      sort: 'created_desc',
      sortOptions: [
        { value: 'name_asc', label: this.$t('IMAGE_LIBRARY.SORT.NAME_ASC') },
        { value: 'created_desc', label: this.$t('IMAGE_LIBRARY.SORT.NEWEST') },
        { value: 'order_asc', label: this.$t('IMAGE_LIBRARY.SORT.CUSTOM') },
        { value: 'images_desc', label: this.$t('IMAGE_LIBRARY.SORT.MOST_IMAGES') },
      ],
      currentLibrary: null,
      showLibraryModal: false,
      editingLibrary: null,
      showUploadModal: false,
      showEditItemModal: false,
      editingItem: null,
      businessCategories: [],
    }
  },
  computed: {
    accountId() { return this.$store.getters.getCurrentAccountId },
    basePath() { return frontendURL(`accounts/${this.accountId}/settings/ai-settings`) },
    libraryId() { return this.$route.params.libraryId },
  },
  watch: {
    '$route.params.libraryId': {
      handler() { this.syncModeFromRoute(); this.load() }, immediate: true
    }
  },
  methods: {
    syncModeFromRoute() {
      this.mode = this.libraryId ? 'item' : 'library'
      this.page = 1
    },
    async load() {
      if (!this.accountId) return
      this.isLoading = true
      try {
        await this.loadBusinessCategories()
        if (this.mode === 'library') await this.loadLibraries()
        else await this.loadItems()
      } finally {
        this.isLoading = false
      }
    },
    async loadBusinessCategories() {
      try {
        const { data } = await window.axios.get(`/api/v1/accounts/${this.accountId}/business_categories`, {
          params: { category_type: 'image_library' }
        })
        this.businessCategories = data.business_categories || []
      } catch (error) {
        console.error('Failed to load business categories:', error)
        this.businessCategories = []
      }
    },
    async loadLibraries() {
      const { data } = await window.axios.get(`/api/v1/accounts/${this.accountId}/image_libraries`, {
        params: { page: this.page, per_page: 12, q: this.q, sort: this.sort, include_business_category: true }
      })
      this.items = data.image_libraries || []
      this.total = data.meta?.total_count || 0
    },
    async loadItems() {
      const { data } = await window.axios.get(`/api/v1/accounts/${this.accountId}/image_libraries/${this.libraryId}/image_items/by_library`, {
        params: { page: this.page, per_page: 12, search: this.q, sort: this.sort }
      })
      this.items = data.image_items || []
      this.total = data.meta?.total_count || 0
      this.currentLibrary = data.library || null
    },
    onPageChange(p) { this.page = p; this.load() },
    onSearch() { clearTimeout(this.searchTimer); this.searchTimer = setTimeout(() => { this.page = 1; this.load() }, 300) },
    onCardClick(item) {
      if (this.mode === 'library') this.$router.push({ params: { libraryId: item.id } })
    },
    async onAction({ type, item }) {
      try {
        if (this.mode === 'library') {
          if (type === 'delete') {
            await window.axios.delete(`/api/v1/accounts/${this.accountId}/image_libraries/${item.id}`)
            this.load()
          } else if (type === 'toggle') {
            await window.axios.post(`/api/v1/accounts/${this.accountId}/image_libraries/${item.id}/toggle_status`)
            this.load()
          } else if (type === 'edit') {
            this.editingLibrary = item
            this.showLibraryModal = true
          }
        } else if (type === 'delete') {
          await window.axios.delete(`/api/v1/accounts/${this.accountId}/image_libraries/${this.libraryId}/image_items/${item.id}`)
          this.load()
        } else if (type === 'toggle') {
          await window.axios.post(`/api/v1/accounts/${this.accountId}/image_libraries/${this.libraryId}/image_items/${item.id}/toggle_status`)
          this.load()
        } else if (type === 'rename' || type === 'edit') {
          this.editingItem = item
          this.showEditItemModal = true
        } else if (type === 'copy_url' && (item.file_url || item.download_url)) {
          await navigator.clipboard.writeText(item.file_url || item.download_url)
        }
      } catch (e) {
        // toast đơn giản
        this.$store.dispatch('setBanner', { type: 'error', message: this.$t('GENERAL.ERROR') })
      }
    },
    openCreateLibrary() { this.editingLibrary = null; this.showLibraryModal = true },
    openUpload() { this.showUploadModal = true },
    
    onCategoryCreated(newCategory) {
      this.businessCategories.push(newCategory);
    },
  }
}
</script>
